import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Send, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });

export const Comments = ({ slug }) => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState({ name: "", text: "", website: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const { data: comments = [], isLoading } = useQuery({
        queryKey: ["comments", slug],
        queryFn: () => axios.get(`${API}/articles/${slug}/comments`).then((r) => r.data),
    });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await axios.post(`${API}/articles/${slug}/comments`, form);
            toast.success(res.data.message);
            setSent(true);
            setForm({ name: "", text: "", website: "" });
        } catch (err) {
            toast.error("Não foi possível enviar o comentário. Tente novamente.");
        } finally {
            setSending(false);
        }
    };

    const inputCls = "w-full bg-paper border border-white/10 focus:border-gold/60 outline-none px-4 py-3 text-sm placeholder:text-slate-600 transition-colors";

    return (
        <section data-testid="comments-section" className="mt-16 pt-10 border-t border-white/10">
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight mb-8 flex items-center gap-3">
                <MessageSquare size={20} className="text-gold" />
                Comentários
                {comments.length > 0 && <span className="text-sm font-sans text-slate-500">({comments.length})</span>}
            </h2>

            {isLoading ? (
                <div className="h-20 bg-white/5 animate-pulse" />
            ) : comments.length === 0 ? (
                <p className="text-sm text-slate-500 mb-10">Seja a primeira pessoa a comentar este guia.</p>
            ) : (
                <div className="flex flex-col gap-5 mb-12">
                    {comments.map((c, i) => (
                        <Reveal key={i} delay={i * 0.04}>
                            <div data-testid={`comment-${i}`} className="border border-white/10 bg-paper p-5">
                                <div className="flex items-center gap-3 mb-2.5">
                                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gold/10 border border-gold/30 text-gold text-xs font-serif">
                                        {c.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                                    </span>
                                    <span className="text-sm font-medium">{c.name}</span>
                                    <span className="text-[11px] text-slate-600 ml-auto">{formatDate(c.created_at)}</span>
                                </div>
                                <p className="text-sm text-slate-300 leading-relaxed">{c.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            )}

            {sent ? (
                <div data-testid="comment-pending-notice" className="flex items-center gap-3 border border-gold/30 bg-gold/[0.05] p-5">
                    <Clock size={18} className="text-gold shrink-0" />
                    <p className="text-sm text-slate-300">Comentário recebido! Ele aparece aqui assim que passar pela moderação.</p>
                </div>
            ) : (
                <form data-testid="comment-form" onSubmit={submit} className="border border-white/10 bg-paper p-6 flex flex-col gap-4">
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Deixe seu comentário</span>
                    <input
                        data-testid="comment-name-input"
                        required
                        minLength={2}
                        maxLength={80}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Seu nome"
                        className={inputCls}
                    />
                    <textarea
                        data-testid="comment-text-input"
                        required
                        minLength={3}
                        maxLength={2000}
                        rows={4}
                        value={form.text}
                        onChange={(e) => setForm({ ...form, text: e.target.value })}
                        placeholder="Dúvida, experiência ou complemento sobre o tema..."
                        className={`${inputCls} resize-y`}
                    />
                    <input
                        type="text"
                        name="website"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        tabIndex={-1}
                        autoComplete="off"
                        style={{ display: "none" }}
                        aria-hidden="true"
                    />
                    <button
                        data-testid="comment-submit-button"
                        type="submit"
                        disabled={sending}
                        className="self-start flex items-center gap-2 bg-gold text-ink font-medium px-6 py-3 text-sm hover:bg-golddark transition-colors disabled:opacity-50"
                    >
                        {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                        Enviar comentário
                    </button>
                    <p className="text-[11px] text-slate-600">Comentários passam por moderação antes de aparecer no site.</p>
                </form>
            )}
        </section>
    );
};
